import { useEffect, useMemo, useRef, useState } from "react";
import { bindData } from "@aelive/data-binding-library";
import { SINGLE_STAT_CAPTIONS, DOUBLE_STAT_CAPTIONS, ALL_MATCHSTATS_CAPTIONS, ADVANCED_STAT_CAPTIONS } from "../config/matchStatsCaptions";
import { resolveAdvancedTile } from "../utils/advancedStatTiles";

// Title-only advanced tiles don't need their caption fetched.
const ADVANCED_VALUE_CAPTIONS = ADVANCED_STAT_CAPTIONS.filter((c) => c.kind !== "title").map((c) => c.name);

// Copilot STATS tiles sourced from the MatchStats_* JSONata captions.
//
// Two phases:
//  1. Fetch each MVP caption's JSON once from `${captionsEndpoint}/captions/<name>.json`
//     (same URL shape Maestro's useCaptionCrawl uses) and keep its raw `data`
//     payload (the object with $fixture / $home1 / MatchStats).
//  2. Whenever the live fixture stats change, run bindData(payload, bindingContext)
//     to resolve the $home/$away variables and the {{...}} templates, then read
//     the display values straight out of the caption's own MatchStats tree.
//
// Titles/values therefore come entirely from the caption definition — the app
// does no stat math of its own (so e.g. possession's $away1 = 100 - $home1 is
// honoured exactly as authored).

const stripFileName = (path) => (typeof path === "string" && path.includes("/") ? path.slice(0, path.lastIndexOf("/")) : null);

// A single caption fetch, unwrapping the { data, actions, ... } envelope.
const fetchCaptionData = async (endpoint, name) => {
  const res = await fetch(`${endpoint}/captions/${name}.json`);
  if (!res.ok) return null;
  const raw = await res.json();
  const isEnvelope = raw?.data && typeof raw.data === "object" && !Array.isArray(raw.data);
  return isEnvelope ? raw.data : raw;
};

// Read a resolved MatchStats stat block (Stat1 / Stat2) into { title, home, away }.
const readStat = (statArray, key) => {
  const stat = statArray?.[key] || {};
  return {
    title: stat.strTitle ?? "",
    home: stat.Team1?.strValue ?? "",
    away: stat.Team2?.strValue ?? "",
  };
};

const buildSingleTile = (name, output) => {
  const ms = output?.MatchStats || {};
  const s1 = readStat(ms.StatArray, "Stat1");
  return { key: name, name, isDouble: false, title: s1.title, home: s1.home, away: s1.away };
};

const buildDoubleTile = (name, output) => {
  const ms = output?.MatchStats || {};
  return {
    key: name,
    name,
    isDouble: true,
    title: ms.strTitle ?? "",
    stat1: readStat(ms.StatArray, "Stat1"),
    stat2: readStat(ms.StatArray, "Stat2"),
  };
};

/**
 * useMatchStatsTiles
 * @param {object}  opts
 * @param {string=} opts.graphicPath    gfx_PermScore graphic path; the caption endpoint is its parent dir.
 * @param {object=} opts.bindingContext Live data context for bindData (fixtureState with a top-level `fixture` array).
 * @param {boolean} opts.enabled        Only fetch/evaluate when the STATS grid is actually shown.
 * @returns {{ single: object[], double: object[], advanced: object[], loading: boolean }}
 */
export function useMatchStatsTiles({ graphicPath, bindingContext, enabled = true }) {
  const endpoint = useMemo(() => stripFileName(graphicPath), [graphicPath]);

  // Phase 1 — fetch raw caption payloads once per endpoint.
  const [rawCaptions, setRawCaptions] = useState({}); // { [name]: payload }
  useEffect(() => {
    if (!enabled || !endpoint) return;
    let cancelled = false;
    (async () => {
      const entries = await Promise.all(
        [...ALL_MATCHSTATS_CAPTIONS, ...ADVANCED_VALUE_CAPTIONS].map(async (name) => {
          try {
            return [name, await fetchCaptionData(endpoint, name)];
          } catch {
            return [name, null];
          }
        }),
      );
      if (cancelled) return;
      const map = {};
      for (const [name, payload] of entries) if (payload) map[name] = payload;
      setRawCaptions(map);
    })();
    return () => { cancelled = true; };
  }, [endpoint, enabled]);

  // Only re-evaluate when the fixture's stats actually change — not on every
  // unrelated fixtureState update (score, clock, events).
  const statsSignature = useMemo(() => {
    const fx = bindingContext?.fixture?.[0];
    if (!fx) return "";
    try {
      const home = fx.home_team?.[0];
      const away = fx.away_team?.[0];
      // Advanced tiles also read win probability, momentum and the opta event feed.
      return JSON.stringify([
        home?.stats ?? null,
        away?.stats ?? null,
        fx.win_probability ?? null,
        fx.momentum ?? null,
        home?.opta_events?.length ?? 0,
        away?.opta_events?.length ?? 0,
      ]);
    } catch {
      return "";
    }
  }, [bindingContext]);

  // Phase 2 — evaluate the fetched captions against the live context.
  const [tiles, setTiles] = useState({ single: [], double: [], advanced: [], loading: true, resolvedByCaption: {} });
  const bindingRef = useRef(bindingContext);
  bindingRef.current = bindingContext;

  useEffect(() => {
    const names = Object.keys(rawCaptions).filter((n) => !ADVANCED_VALUE_CAPTIONS.includes(n));
    if (!enabled || names.length === 0 || !bindingRef.current) {
      setTiles((prev) => (prev.single.length || prev.double.length || prev.advanced.length
        ? { single: [], double: [], advanced: [], loading: !names.length, resolvedByCaption: {} }
        : prev));
      return;
    }
    let cancelled = false;
    (async () => {
      const resolved = {};
      await Promise.all(
        names.map(async (name) => {
          try {
            const { output } = await bindData(rawCaptions[name], bindingRef.current);
            resolved[name] = output;
          } catch {
            resolved[name] = null;
          }
        }),
      );
      const context = bindingRef.current;
      const advanced = await Promise.all(
        ADVANCED_STAT_CAPTIONS.map((entry) =>
          // A caption that fails to resolve still shows as a title-only tile.
          resolveAdvancedTile(entry, rawCaptions[entry.name], context).catch(() =>
            resolveAdvancedTile({ ...entry, kind: "title" }),
          ),
        ),
      );
      if (cancelled) return;
      // Preserve the MVP order; drop captions that failed to fetch/resolve.
      const single = SINGLE_STAT_CAPTIONS.filter((n) => resolved[n]).map((n) => buildSingleTile(n, resolved[n]));
      const double = DOUBLE_STAT_CAPTIONS.filter((n) => resolved[n]).map((n) => buildDoubleTile(n, resolved[n]));
      // resolvedByCaption is the resolved caption body ({ MatchStats: {...} })
      // per caption — the headless animator seeds it as the provider's
      // initialFormData so the BAKE ships real data (not an empty {}).
      const resolvedByCaption = {};
      for (const n of names) if (resolved[n]) resolvedByCaption[n] = resolved[n];
      setTiles({ single, double, advanced, loading: false, resolvedByCaption });
    })();
    return () => { cancelled = true; };
  }, [rawCaptions, statsSignature, enabled]);

  return tiles;
}
